import { Separator } from '@fluentui/react';
import { RouterParams } from 'App';
import { RootState } from 'app/store';
import PageLoading from 'global/PageLoading';
import { IComponentContextMenu } from 'model/Application';
import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import SnapshotContent from './components/SnapshotContent';
import SnapshotDialogs from './components/SnapshotDialogs';
import SnapshotNavbar from './components/SnapshotNavbar';
import SnapshotNotifications from './components/SnapshotNotifications';
import { useLoadSnapshot } from './hooks/useLoadSnapshot';

interface IGroupConfigurationSnapshotContextProps extends IComponentContextMenu {
    children: React.ReactNode;
    isChecklist?: boolean;
    hideShowDeactivated?: boolean;
    unStickyNavbar?: boolean;
    hideSave?: boolean;
    isMergeApp?: boolean;
    isCompareApp?: boolean;
    isUnderwritingReviewApp?: boolean;
}

/**
 * Main context component for group configuration snapshots
 * Orchestrates loading, dialogs, and rendering of child components
 * 
 * Refactored for better:
 * - Performance (memoized sub-components, lazy data source initialization)
 * - Maintainability (extracted concerns into sub-components and custom hooks)
 * - Readability (reduced component complexity, clearer logic flow)
 */
const GroupConfigurationSnapshotContext = memo((props: IGroupConfigurationSnapshotContextProps) => {
    const { id: accountId, snapshotId } = useParams<RouterParams>();
    const state = useSelector((state: RootState) => state);

    // Custom hook handles all snapshot loading logic with memoized data source
    const { dataSource, isLoading } = useLoadSnapshot({ accountId, snapshotId });

    // Show loading state while snapshot is being fetched
    if (isLoading) {
        return <PageLoading />;
    }

    // Ensure required data is available
    if (!state?.data?.json?.fields) {
        return null;
    }

    const isViewTypeSelected = state.status.viewType !== null;

    return (
        <>
            <SnapshotNotifications dataSavingStatus={state.status.dataSavingStatus} />
            <SnapshotNavbar {...props} />
            <SnapshotDialogs
                isChecklist={props.isChecklist}
                dataSource={dataSource}
                isMergeApp={props.isMergeApp}
                isCompareApp={props.isCompareApp}
            />
            <Separator />
            <SnapshotContent
                isVisible={isViewTypeSelected}
                isMergeApp={props.isMergeApp}
                isCompareApp={props.isCompareApp}
                isUnderwritingReviewApp={props.isUnderwritingReviewApp}
            >
                {props.children}
            </SnapshotContent>
            {state.status.processing &&
                <PageLoading />
            }
        </>
    );
});

GroupConfigurationSnapshotContext.displayName = 'GroupConfigurationSnapshotContext';

export default GroupConfigurationSnapshotContext;

